// client/src/App.jsx
// Top-level routing + auth gate.
//
// Two route trees:
//
//   /redeem
//     PUBLIC. The voucher redemption page is reachable by anyone with
//     a printed voucher — no Google session required. Rendered above
//     the auth gate so anonymous visitors never see the LoginScreen
//     when their intent was just to redeem a coupon.
//
//   everything else
//     Goes through <AuthedApp>: useMe → LoginScreen for anon →
//     NoAccessScreen for authed-but-not-provisioned → Layout +
//     CampsListPage / CampDetailPage / ApplyPage for managers.

import { Routes, Route, Navigate } from 'react-router-dom';
import { useMe } from './hooks/useMe.js';
import Layout from './components/Layout.jsx';
import LoginScreen from './components/LoginScreen.jsx';
import LoginScreenV2 from './components/LoginScreenV2.jsx';
import Spinner from './components/Spinner.jsx';
import CampsListPage from './pages/CampsListPage.jsx';
import CampDetailPage from './pages/CampDetailPage.jsx';
import OrderCardsPage from './pages/OrderCardsPage.jsx';
import AdminCardOrdersPage from './pages/AdminCardOrdersPage.jsx';
import ApplyPage from './pages/ApplyPage.jsx';
import RedeemPage from './pages/RedeemPage.jsx';
import NoAccessScreen from './components/NoAccessScreen.jsx';

export default function App() {
  return (
    <Routes>
      {/* PUBLIC — no auth check, no /api/me call. */}
      <Route path="/redeem" element={<RedeemPage />} />
      {/* TEMP preview of the login redesign — remove when V2 replaces V1. */}
      <Route path="/login-new" element={<LoginScreenV2 />} />
      <Route path="*" element={<AuthedApp />} />
    </Routes>
  );
}

function AuthedApp() {
  const { data: user, isLoading, error } = useMe();

  if (isLoading) {
    return (
      <main className="min-h-screen grid place-items-center">
        <Spinner label="טוען..." />
      </main>
    );
  }

  if (error) {
    return (
      <main className="min-h-screen grid place-items-center p-6">
        <div className="card-pop max-w-md w-full p-8 text-center">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-joy-100 text-joy-700 grid place-items-center mb-3">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.008M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
          <h1 className="text-xl font-extrabold text-ink-900">שגיאת התחברות</h1>
          <p className="mt-2 text-ink-600">{error.message || 'משהו לא תקין.'}</p>
          <button
            type="button"
            className="btn-primary mt-5"
            onClick={() => window.location.reload()}
          >
            ניסיון חוזר
          </button>
        </div>
      </main>
    );
  }

  if (!user) return <LoginScreen />;

  // A user is "provisioned" if they're a Super Admin or have at least
  // one camp assigned to them. Anyone else (a fresh Google account that
  // hasn't been invited) sees NoAccessScreen instead of the empty
  // back-office.
  //
  // /apply is the one exception: even an unprovisioned user must be
  // able to reach the signup form, since that's how they go from
  // "no access" to "applied".
  const hasAccess =
    !!user.isSuperAdmin ||
    (Array.isArray(user.managedCamps) && user.managedCamps.length > 0);

  return (
    <Routes>
      <Route path="/apply" element={<ApplyPage user={user} />} />
      {hasAccess ? (
        <Route
          path="*"
          element={
            <Layout user={user}>
              <Routes>
                <Route path="/" element={<CampsListPage user={user} />} />
                <Route path="/camps/:campId" element={<CampDetailPage user={user} />} />
                <Route path="/camps/:campId/order-cards" element={<OrderCardsPage />} />
                {/* Section tabs are real URLs (deep-linkable, back-button works,
                    refresh keeps the tab). The static order-cards route above
                    out-ranks this dynamic :tab segment in React Router. */}
                <Route path="/camps/:campId/:tab" element={<CampDetailPage user={user} />} />
                {user.isSuperAdmin && (
                  <Route path="/admin/card-orders" element={<AdminCardOrdersPage />} />
                )}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
          }
        />
      ) : (
        <Route path="*" element={<NoAccessScreen user={user} />} />
      )}
    </Routes>
  );
}
